import React, {memo, useRef, useCallback, useState, useEffect} from 'react';
import {DeliveryTaskOrderType} from '../../graphql/types/DeliveryTaskOrderType';
import {ExceptionDictionaryType, ExceptionSubReasonType} from '../../graphql/queries/getExceptionDictionaryForDelivery';
import OptimizedOrderGroup from './OptimizedOrderGroup';
import {t} from '../../../../i18n';

interface OrderGroup {
    fullName: string;
    phone: string;
    address: string;
    orders: DeliveryTaskOrderType[];
}

interface VirtualOrderListProps {
    orderGroups: OrderGroup[];
    isExpanded: boolean;
    expandedOrders: Set<number>;
    exceptionReason?: ExceptionDictionaryType;
    exceptionSubReasons?: ExceptionSubReasonType[];
    onToggleOrder: (orderId: number) => void;
}

const BATCH_SIZE = 50; // 每批加载50条
const SCROLL_THRESHOLD = 200; // 距离底部200px时加载更多

const VirtualOrderList: React.FC<VirtualOrderListProps> = memo(({
    orderGroups,
    isExpanded,
    expandedOrders,
    exceptionReason,
    exceptionSubReasons,
    onToggleOrder
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [displayLimit, setDisplayLimit] = useState(BATCH_SIZE);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // 计算总订单数
    const totalOrderCount = orderGroups.reduce((sum, group) => sum + group.orders.length, 0);

    // 滚动加载更多
    const handleScroll = useCallback(() => {
        if (!isExpanded || isLoadingMore) return;
        if (displayLimit >= totalOrderCount) return;

        const container = containerRef.current;
        if (!container) return;

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        // 距离底部小于阈值时加载更多
        if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
            setIsLoadingMore(true);
            // 模拟异步加载
            setTimeout(() => {
                setDisplayLimit(prev => Math.min(prev + BATCH_SIZE, totalOrderCount));
                setIsLoadingMore(false);
            }, 100);
        }
    }, [isExpanded, isLoadingMore, displayLimit, totalOrderCount]);

    // 监听滚动事件
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // 展开状态变化时重置显示数量
    useEffect(() => {
        if (isExpanded) {
            setDisplayLimit(BATCH_SIZE);
        }
    }, [isExpanded]);

    // 渲染逻辑
    const maxDisplay = isExpanded ? displayLimit : 2;
    const displayedCountRef = useRef(0);
    displayedCountRef.current = 0; // 重置计数

    return (
        <div ref={containerRef} className="max-h-[40vh] overflow-y-auto">
            {orderGroups.map((group) => (
                <OptimizedOrderGroup
                    key={`${group.fullName}_${group.phone}`}
                    group={group}
                    isExpanded={isExpanded}
                    maxDisplay={maxDisplay}
                    expandedOrders={expandedOrders}
                    exceptionReason={exceptionReason}
                    exceptionSubReasons={exceptionSubReasons}
                    displayedCountRef={displayedCountRef}
                    onToggleOrder={onToggleOrder}
                />
            ))}
            
            {/* 加载状态动画 */}
            {isExpanded && (
                <>
                    {isLoadingMore && (
                        <div className="flex justify-center items-center py-3">
                            <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                            </div>
                        </div>
                    )}
                    {displayLimit >= totalOrderCount && totalOrderCount > BATCH_SIZE && (
                        <div className="text-center py-1 text-xs text-gray-300">
                            {t('pages.delivery.temporary.common.allDisplayed')}
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

VirtualOrderList.displayName = 'VirtualOrderList';

export default VirtualOrderList;
